/**
 * TestlabFullAnalysisPage - Interface för AuditAI Full Analysis testmodul
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Square, FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TestScenarioRunner } from '@/modules/audit-full-analysis/TestScenarioRunner';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export function TestlabFullAnalysisPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [activeRevisions, setActiveRevisions] = useState<string[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dummyReport, setDummyReport] = useState<string>('');
  const [testStartTime, setTestStartTime] = useState<Date | null>(null);
  const [polledOnce, setPolledOnce] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const scenarioRunnerRef = React.useRef<TestScenarioRunner | null>(null);
  const getRunner = () => {
    if (!scenarioRunnerRef.current) scenarioRunnerRef.current = new TestScenarioRunner();
    return scenarioRunnerRef.current;
  };

  // Håll koll på auth-status för att guida användaren
  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setIsAuthenticated(!!user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const startStandardTest = async () => {
    try {
      // Säkerställ att användaren är inloggad innan vi startar
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: 'Logga in krävs',
          description: 'Du måste vara inloggad för att köra simuleringen.',
          variant: 'destructive',
        });
        setIsRunning(false);
        return;
      }

      setIsRunning(true);
      setTestStartTime(new Date());
      console.log('🚀 Starting test and workers from UI...');
      const revisionIds = await getRunner().runStandardTest();
      console.log('🆔 Got revision IDs:', revisionIds);
      setActiveRevisions(revisionIds);
      toast({
        title: 'Test startat',
        description: `Skapade ${revisionIds.length} revisioner, workers körs`,
      });
    } catch (error: any) {
      console.error('❌ Test error:', error);
      toast({
        title: 'Kunde inte starta testet',
        description: error?.message || 'Ett fel uppstod vid start.',
        variant: 'destructive',
      });
      setIsRunning(false);
    }
  };
  const stopTest = async () => {
    try {
      await getRunner().stopTest();
      setIsRunning(false);
      setActiveRevisions([]);
      setTestStartTime(null);
    } catch (error) {
      console.error('❌ Stop test error:', error);
    }
  };

  const generateDummyReport = async () => {
    if (activeRevisions.length > 0) {
      const html = await getRunner().generateDummyReport(activeRevisions[0]);
      setDummyReport(html);
      toast({ title: 'Rapport genererad', description: 'Testrapport har genererats' });
    } else {
      toast({ title: 'Ingen revision', description: 'Starta testet först för att generera rapport.', variant: 'destructive' });
    }
  };
  const clearActiveTests = async () => {
    try {
      // Hämta aktiva testrevisioner
      const { data: revisions, error } = await supabase
        .from('revisions')
        .select('id')
        .like('company_name', 'Test Företag%')
        .not('status', 'in', '(completed,cancelled)');

      if (error) throw error;
      const ids = (revisions || []).map(r => r.id);
      if (ids.length === 0) {
        toast({ title: 'Inget att rensa', description: 'Inga aktiva testrevisioner hittades.' });
        return;
      }

      // Ta bort köjobb och batchar för respektive revision
      for (const id of ids) {
        await supabase.from('worker_queue').delete().like('job_id', `${id}%`);
        await supabase.from('worker_queue').delete().contains('payload', { revision_id: id } as any);
      }
      await supabase.from('batches').delete().in('revision_id', ids);
      await supabase.from('aggregates').delete().in('revision_id', ids);
      await supabase.from('revision_reports').delete().in('revision_id', ids);

      // Avsluta revisionerna
      await supabase
        .from('revisions')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .in('id', ids);

      setActiveRevisions([]);
      const currentStats = await getRunner().getRealtimeStats();
      setStats(currentStats);
      toast({ title: 'Rensat', description: `Rensade ${ids.length} aktiva testrevisioner.` });
    } catch (err: any) {
      console.error('❌ Clear error:', err);
      toast({ title: 'Kunde inte rensa', description: err?.message || 'Ett fel uppstod.', variant: 'destructive' });
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    const fetchStats = async () => {
      console.log('🔄 Fetching stats, activeRevisions:', activeRevisions.length);
      try {
        const currentStats = await getRunner().getRealtimeStats();
        console.log('📊 Got stats:', currentStats);
        setStats(currentStats);
        setPolledOnce(true);
      } catch (error) {
        console.error('❌ Stats error:', error);
      }
    };

    if (isRunning || activeRevisions.length > 0) {
      // Hämta stats direkt först
      fetchStats();
      
      // Sedan på intervall
      interval = setInterval(fetchStats, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeRevisions, isRunning]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/testlab" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka till Testlab
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">AuditAI Full Analysis</h1>
              <p className="text-muted-foreground mt-1">
                Testmodul för fullständig revision med batchning och kösystem
              </p>
            </div>
            <Badge variant="secondary" className="ml-auto">TESTLAB</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Varningsbanderoll */}
        <Alert className="mb-6 border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Testläge aktivt:</strong> Denna modul simulerar fullständig revision med AI-analys och kostnadskontroll.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Kontrollpanel */}
          <Card>
            <CardHeader>
              <CardTitle>Testkontroll</CardTitle>
              <CardDescription>
                Starta simulering av 3 simultana revisioner med 10 batchar var
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {testStartTime && (
                <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded">
                  <div className="flex justify-between">
                    <span>Test startad:</span>
                    <span>{testStartTime.toLocaleTimeString('sv-SE')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Förväntad tid:</span>
                    <span>2-5 minuter</span>
                  </div>
                </div>
              )}
              
              <Button 
                onClick={startStandardTest} 
                disabled={isRunning || !isAuthenticated}
                className="w-full"
              >
                {isRunning ? (
                  <>
                    <Square className="h-4 w-4 mr-2 animate-pulse" />
                    Kör test... (Workers aktiva)
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {isAuthenticated ? 'Starta standardtest' : 'Logga in för att starta'}
                  </>
                )}
              </Button>

              {!isAuthenticated && (
                <div className="text-sm text-muted-foreground">
                  Du måste vara inloggad för att skapa revisioner.
                </div>
              )}

              {isRunning && (
                <Button 
                  variant="destructive" 
                  onClick={stopTest}
                  className="w-full"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stoppa test
                </Button>
              )}

              {activeRevisions.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={generateDummyReport}
                  className="w-full"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Generera testrapport
                </Button>
              )}

              <Button 
                variant="destructive"
                onClick={clearActiveTests}
                className="w-full"
              >
                Rensa aktiva revisioner
              </Button>
            </CardContent>
          </Card>

          {/* Realtidsstatistik */}
          <Card>
            <CardHeader>
              <CardTitle>Realtidsstatistik</CardTitle>
              <CardDescription>
                Live-uppdatering av pågående revisioner
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats && stats.active_revisions > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{stats.active_revisions}</div>
                      <div className="text-sm text-muted-foreground">Aktiva revisioner</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{stats.total_cost?.toFixed(2) || '0.00'} SEK</div>
                      <div className="text-sm text-muted-foreground">Total kostnad</div>
                    </div>
                    <div>  
                      <div className="text-2xl font-bold text-purple-600">{stats.total_tokens?.toLocaleString() || '0'}</div>
                      <div className="text-sm text-muted-foreground">Tokens</div>
                    </div>
                  </div>

                  {stats.revisions.map((rev: any, i: number) => (
                    <div key={rev.id} className="border rounded p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{rev.company_name}</span>
                        <Badge variant={rev.status === 'completed' ? 'default' : rev.status === 'failed' ? 'destructive' : 'secondary'}>
                          {rev.status}
                        </Badge>
                      </div>
                      {rev.progress && (
                        <div className="space-y-1">
                          <Progress 
                            value={rev.progress.percentage || 0} 
                            className="h-2"
                          />
                          <div className="text-xs text-muted-foreground">
                            Batchar: {rev.progress.batches_completed || 0}/{rev.progress.batches_total || 0}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : polledOnce ? (
                <div className="text-center text-muted-foreground py-8">
                  <div>Ingen aktiv simulering</div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  {isRunning ? (
                    <div className="space-y-2">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                      <div>Startar test...</div>
                    </div>
                  ) : (
                    <div>Ingen aktiv simulering</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dummy rapport */}
        {dummyReport && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Testrapport Preview</CardTitle>
              <CardDescription>
                Simulerad slutrapport (i produktion skulle detta vara AI-genererat)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <iframe
                title="Testrapport Preview"
                srcDoc={dummyReport}
                sandbox="allow-same-origin"
                className="w-full h-96 rounded border bg-white"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}